import { useCallback } from 'react';
import {
  configApiRef,
  discoveryApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { UserIdentity } from '@backstage/core-components';
import { logtoOIDCAuthApiRef } from '../../apis';
import { enqueueSnackbar } from 'notistack';

const NO_ACCESS_MESSAGE =
  'You do not have necessary permissions to login. Please contact your Administrator';

export function useOpenLogin(onSignInSuccess?: (identity: any) => void) {
  const configApi = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const logtoAuthApi = useApi(logtoOIDCAuthApiRef);

  const loginViaNewTab = useCallback(async () => {
    const authTab = window.open('', '_blank');
    if (!authTab) return;

    try {
      const baseUrl = await discoveryApi.getBaseUrl('auth');
      const env = configApi.getOptionalString('auth.environment');
      const params = new URLSearchParams({
        origin: window.location.origin,
        flow: 'popup',
      });
      if (env) params.set('env', env);
      const authUrl = `${baseUrl}/logto-provider/start?${params}`;
      const authOrigin = new URL(baseUrl).origin;

      authTab.location.href = authUrl;

      await new Promise<void>((resolve, reject) => {
        // eslint-disable-next-line prefer-const
        let closedPoller: ReturnType<typeof setInterval>;
        const onMessage = (event: MessageEvent) => {
          if (event.source !== authTab) return;
          if (event.origin !== authOrigin) return;
          const { data } = event;
          if (data.type !== 'authorization_response') return;
          window.removeEventListener('message', onMessage);
          clearInterval(closedPoller);
          if ('error' in data) reject(new Error(data.error.message));
          else resolve();
        };
        closedPoller = setInterval(() => {
          if (authTab.closed) {
            window.removeEventListener('message', onMessage);
            clearInterval(closedPoller);
            reject(new Error('Auth tab was closed'));
          }
        }, 500);
        window.addEventListener('message', onMessage);
      });

      const identityResponse = await logtoAuthApi.getBackstageIdentity({
        optional: true,
      });
      if (!identityResponse) return;
      const profile = await logtoAuthApi.getProfile();
      onSignInSuccess?.(
        UserIdentity.create({
          identity: identityResponse.identity,
          authApi: logtoAuthApi,
          profile,
        }),
      );
    } catch (error) {
      authTab.close();
      if (error instanceof Error && error.message !== 'Auth tab was closed') {
        enqueueSnackbar(NO_ACCESS_MESSAGE, {
          variant: 'error',
          autoHideDuration: 7000,
        });
      }
    }
  }, [logtoAuthApi, discoveryApi, configApi, onSignInSuccess]);

  const loginViaPopup = useCallback(async () => {
    try {
      const identityResponse = await logtoAuthApi.getBackstageIdentity({
        instantPopup: true,
      });
      if (!identityResponse) return;
      const profile = await logtoAuthApi.getProfile();
      onSignInSuccess?.(
        UserIdentity.create({
          identity: identityResponse.identity,
          authApi: logtoAuthApi,
          profile,
        }),
      );
    } catch (error) {
      const name = error instanceof Error ? (error as any).name : '';
      if (name === 'PopupClosedError' || name === 'PopupRejectedError') return;

      enqueueSnackbar(NO_ACCESS_MESSAGE, {
        variant: 'error',
        autoHideDuration: 7000,
      });
    }
  }, [logtoAuthApi, onSignInSuccess]);

  const loginNewTab = Boolean(
    configApi.getOptionalBoolean('featureFlags.loginNewTab'),
  );

  return loginNewTab ? loginViaNewTab : loginViaPopup;
}
